//@flow
import classnames from 'classnames';
import compose from 'recompose/compose';
import { Button, ButtonTypes } from 'common/components/core/button';
import { EnhancedField } from 'common/components/core/form/Field';
import { BasicDimensionsInput } from 'common/entity/components/DimensionsInput';
import { withUnitConverter } from 'common/containers/intl';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { Dimensions } from 'common/models/internal/Dimensions';
import {
    Volume,
    VolumeUnitType,
    sourceUnitForVolume
} from 'common/models/internal/Volume';
import React, { Component } from 'react';
import { themr, type ThemeType } from 'react-css-themr';
import { translate, type TFunction } from 'react-i18next';
import { Icon } from 'common/components/core/icon';

import theme from './VolumeInput.scss';

import {
    VolumeUnitEnum as VolumeEnum,
    type VolumeUnitEnumType
} from 'common/enums/VolumeUnitEnum';

type VolumeInputProps = {
    id: number,
    theme: ThemeType,
    volume: Volume,
    dimensions: Dimensions,
    deletable?: boolean,
    required?: boolean,
    hideVolumeInput?: boolean,
    onChange: (number, Volume) => void,
    unitConverter: UnitConversionHelper,
    onDelete?: number => void,
    t: TFunction,
    error?: string,
    translationsObject: { +[string]: string },
    volumeUnit: VolumeUnitEnumType,
    // Added this support as conversion is not yet handled in Vehicle Model Entity
    // TODO: remove this once conversion is handled in Vehicle Model Entity
    preferredVolumeUnit?: $FixMe,
    showVolumeUnit?: boolean,
    distanceUnit?: string
};

class VolumeInput extends Component<VolumeInputProps> {
    static defaultProps = {
        required: true
    };
    onUnitTypeChange = (selectedOption: $Values<typeof VolumeUnitType>) => {
        const { id, onChange } = this.props;
        let value = '0';
        if (selectedOption === VolumeUnitType.LBHMeters) {
            value = null;
        }
        onChange(id, Volume.fromJS({ unit: selectedOption, value }));
    };

    onDelete = () => {
        const { id, onDelete } = this.props;
        onDelete && onDelete(id);
    };

    handleValueChange = (value: string) => {
        const { onChange, volume, id } = this.props;
        const volumeInSourceUnit = volume.setValue(value);
        onChange(id, volumeInSourceUnit);
    };

    handleLengthChange = (length: string) => {
        const { id, onDimensionsChange, dimensions } = this.props;
        const updatedDimensions = dimensions.setLength(length);
        onDimensionsChange(id, updatedDimensions);
    };

    handleBreadthChange = (breadth: string) => {
        const { id, onDimensionsChange, dimensions } = this.props;

        const updatedDimensions = dimensions.setBreadth(breadth);
        onDimensionsChange(id, updatedDimensions);
    };

    handleHeightChange = (height: string) => {
        const { id, onDimensionsChange, dimensions } = this.props;
        const updatedDimensions = dimensions.setHeight(height);
        onDimensionsChange(id, updatedDimensions);
    };

    getVolumeError = () => {
        const { dimensions, volume, volumeUnit } = this.props;
        if (!dimensions.hasValidationErrors() && dimensions.isFilled()) {
            const { value } = volume;
            if (
                dimensions.getTotalVolume() === Number(value) ||
                volumeUnit !== VolumeEnum.MetersCubic
            ) {
                return false;
            }
            return true;
        }
        return false;
    };

    recalculateVolume = () => {
        const { dimensions, onChange, id, volume } = this.props;
        const updatedVolume = dimensions.getTotalVolume();
        onChange(id, volume.setValue(updatedVolume));
    };

    getDimensionsError = dimension => {
        const { dimensions } = this.props;
        return dimensions.getValidationMessage(dimension);
    };

    renderInputSection() {
        const {
            volume,
            theme,
            t,
            error,
            hideVolumeInput,
            required,
            dimensions,
            translationsObject,
            testId,
            unitConverter,
            showVolumeUnit,
            distanceUnit,
            preferredVolumeUnit = unitConverter.getPreferredUnit({
                sourceUnit: sourceUnitForVolume
            })
        } = this.props;
        return (
            <div>
                <div className={theme.dimensionsInput}>
                    <BasicDimensionsInput
                        testId={testId}
                        t={t}
                        theme={theme}
                        length={dimensions.length || null}
                        breadth={dimensions.breadth || null}
                        height={dimensions.height || null}
                        unitText={distanceUnit.abbr}
                        lengthError={this.getDimensionsError('length')}
                        breadthError={this.getDimensionsError('breadth')}
                        heightError={this.getDimensionsError('height')}
                        onLengthChange={this.handleLengthChange}
                        onBreadthChange={this.handleBreadthChange}
                        onHeightChange={this.handleHeightChange}
                    />
                </div>
                {hideVolumeInput ? null : (
                    <>
                        <span
                            className={classnames(theme.label, theme.subLabel)}
                        >
                            {t(translationsObject.volumeLabel)}
                            {required ? '*' : ''}
                        </span>
                        <div
                            className={classnames(
                                theme.fieldGroup,
                                theme.halfWidth
                            )}
                        >
                            <EnhancedField
                                type="text"
                                element="input"
                                value={volume.value || ''}
                                onChange={this.handleValueChange}
                                theme={theme}
                                error={
                                    volume.getValidationMessage('value') ||
                                    error
                                }
                                rightIcon={
                                    showVolumeUnit
                                        ? preferredVolumeUnit?.abbr
                                        : null
                                }
                            />
                        </div>
                        {this.getVolumeError() ? (
                            <div className={theme.warningMessage}>
                                <Icon icon="alert" theme={theme} />
                                <span className={theme.warningText}>
                                    {t('volumeInput.volumeNotInSync')}

                                    <Button
                                        className={classnames(
                                            theme.recalculateButton
                                        )}
                                        ripple={false}
                                        semibold
                                        buttonType={ButtonTypes.Link}
                                        onClick={this.recalculateVolume}
                                        label={t('volumeInput.recalculate')}
                                        theme={theme}
                                    />
                                </span>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        );
    }

    render() {
        const { theme, deletable } = this.props;
        return (
            <div className={theme.volumeContainer}>
                <div className={theme.topRow}>
                    {deletable ? (
                        <Button
                            buttonType={ButtonTypes.Icon}
                            circle
                            icon="delete-bin"
                            className={theme.deleteBtn}
                            theme={theme}
                            onClick={this.onDelete}
                        />
                    ) : (
                        []
                    )}
                </div>
                {this.renderInputSection()}
            </div>
        );
    }
}

const enhance = compose(
    themr(null, theme, { withRef: true }),
    withUnitConverter,
    translate('entityManagement')
);

const ThemedVolumeInput = enhance(VolumeInput);

export { ThemedVolumeInput as VolumeInput };
